// Pure helpers for AppHost.vue's handling of Trade Hub trip share links. Framework-free so
// they can be unit-tested directly -- see tradeHubShareLink.spec.js.
//
// Background (MYS-7449): a share link (/app/trade-hub/?shareToken=...) opened by someone who
// registers or logs in on the way, but has not unlocked Trade Hub yet (no signed Tour
// Operator contract), cannot open Trade Hub -- /tradehub-token refuses with
// TRADEHUB_NOT_UNLOCKED. AppHost then sends them to the dashboard to finish onboarding and
// parks the share token here, so the first Trade Hub open after unlocking still lands on the
// guest's duplicate-trip dialog instead of the link being silently lost.

export const PENDING_SHARE_TOKEN_KEY = 'tradeHubPendingShareToken'
export const MAX_PENDING_AGE_MS = 7 * 24 * 60 * 60 * 1000

// The share token carried by a guest deep-link hash, in any of the shapes AppHost deals
// with: '#/?shareToken=…', '?shareToken=…', 'trip/<id>?shareToken=…'.
export function getShareTokenFromHash(hash) {
  const queryIndex = (hash ?? '').indexOf('?')
  if (queryIndex < 0) return null
  return new URLSearchParams(hash.substring(queryIndex + 1)).get('shareToken') || null
}

// The guest deep-link hash that opens trade-hub-app with the given share token -- the same
// shape AppHost.mounted() builds for a share link opened cold.
export function toShareTokenHash(token) {
  return `?${new URLSearchParams({ shareToken: token }).toString()}`
}

export function savePendingShareToken(storage, { token, userId, now = Date.now() }) {
  if (!token || !userId) return
  try {
    storage.setItem(PENDING_SHARE_TOKEN_KEY, JSON.stringify({ token, userId, createdAt: now }))
  }
  catch {
    // Best effort -- worst case the user has to open the share link again.
  }
}

// The parked share token, but only for the user who parked it and only while it is fresh;
// anything else is dropped so it can never resurface for someone else on a shared browser.
export function getPendingShareToken(storage, userId, now = Date.now()) {
  let pending = null
  try {
    pending = JSON.parse(storage.getItem(PENDING_SHARE_TOKEN_KEY) ?? 'null')
  }
  catch {
    // Unreadable -> treated like an invalid entry below.
  }
  if (!pending) return null

  const isValid = typeof pending.token === 'string'
    && pending.userId === userId
    && typeof pending.createdAt === 'number'
    && now - pending.createdAt <= MAX_PENDING_AGE_MS
  if (!isValid) {
    clearPendingShareToken(storage)
    return null
  }
  return pending.token
}

export function clearPendingShareToken(storage) {
  try {
    storage.removeItem(PENDING_SHARE_TOKEN_KEY)
  }
  catch {
    // Nothing to clean up.
  }
}
